/**
 * «Escribir con IA» en el panel de un grupo de Mensajes (Alan, 01/11/2026: la IA escribe y el operador aprueba).
 *
 * - Sólo aparece si el servidor tiene la IA prendida (`ia.disponible` de la vista): sin la clave de Anthropic, Mensajes
 *   sigue con el catálogo de textos y «Otra versión».
 * - Pide la propuesta con la oferta, el mínimo y el cupón como están en la pantalla, más las indicaciones opcionales
 *   («más corto», «más formal»). La IA escribe con los datos de la marca y del grupo; nunca con los de una
 *   persona.
 * - Lo que vuelve lo pone la pantalla en el grupo (el mail en el asunto y el texto; la plantilla en su recuadro) para que
 *   el operador lo lea y lo corrija. Nada se guarda ni se manda solo.
 * - Los errores, en palabras del operador: no está prendida, está ocupada, no responde, no quiso, el tope de la hora.
 */

import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta } from "../../wa-oficial/components/estadisticas/comun";
import { mensajesApi } from "../api";
import { INDICACIONES_MAX, textoErrorIa } from "../logic/ia";
import type { Borrador } from "../logic/mensajes";
import type { RespuestaIa } from "../types";

const btn =
  "min-h-[44px] px-3.5 rounded-xl bg-violet-600 text-sm font-semibold text-white hover:bg-violet-700 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

export function EscribirConIa({
  campanaId,
  grupoId,
  b,
  alEscribir,
}: {
  campanaId: string;
  grupoId: string;
  b: Borrador;
  /** lo que propuso la IA: la pantalla lo pone en el grupo */
  alEscribir: (r: RespuestaIa) => void;
}) {
  const [indicaciones, setIndicaciones] = useState("");
  const [escribiendo, setEscribiendo] = useState(false);
  const [error, setError] = useState("");
  const [conAvisos, setConAvisos] = useState(false);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  const esMail = b.canal === "email";
  const yaHay = esMail ? false : !!b.propuestaIa;

  const escribir = async () => {
    setEscribiendo(true);
    setError("");
    setConAvisos(false);
    const l = await llamar<RespuestaIa>(() =>
      mensajesApi.ia(campanaId, {
        grupoId,
        canal: b.canal,
        oferta: b.oferta,
        minimo: b.oferta > 0 ? b.minimo : null,
        cuponId: b.oferta > 0 ? b.cuponId : null,
        indicaciones: indicaciones.trim(),
      }),
    );
    if (!vivo.current) {
      // el operador cambió de grupo mientras escribía: igual le llega a su grupo
      if (l.ok) alEscribir(l.data);
      return;
    }
    setEscribiendo(false);
    if (!l.ok) {
      setError(textoErrorIa(l, "No pudimos pedirle el mensaje a la IA. Probá de nuevo."));
      return;
    }
    setConAvisos(!l.data.lista);
    alEscribir(l.data);
  };

  return (
    <section className="space-y-3 rounded-xl border border-violet-200 bg-violet-50/50 p-4" aria-labelledby={`ia-${grupoId}`} data-testid="escribir-con-ia">
      <div className="space-y-1">
        <h3 id={`ia-${grupoId}`} className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <Sparkles size={17} className="text-violet-700" aria-hidden="true" />
          Escribir con IA
        </h3>
        <p className="text-sm text-slate-600">
          {esMail
            ? "La IA escribe el asunto y el texto con los datos de tu marca y de este grupo. Leelo y corregilo antes de usarlo: nada sale solo."
            : "La IA propone una plantilla para Meta con los datos de tu marca y de este grupo. Leela y corregila: no se manda a Meta sola."}
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block min-w-0 flex-[1_1_260px] space-y-1.5">
          <span className="block text-sm font-semibold text-slate-900">Indicaciones (opcional)</span>
          <input
            value={indicaciones}
            maxLength={INDICACIONES_MAX}
            disabled={escribiendo}
            onChange={(e) => setIndicaciones(e.target.value)}
            placeholder="Por ejemplo: más corto, o más formal"
            className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[15px] text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:bg-slate-50"
          />
        </label>
        <button type="button" className={btn} onClick={() => void escribir()} disabled={escribiendo}>
          {escribiendo ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Sparkles size={16} aria-hidden="true" />}
          {escribiendo ? "Escribiendo…" : yaHay ? "Escribir otra con IA" : "Escribir con IA"}
        </button>
      </div>
      {escribiendo && (
        <p className="text-sm text-slate-600" role="status">
          La IA está escribiendo. Puede tardar unos segundos.
        </p>
      )}
      {error && <ErrorTarjeta texto={error} />}
      {conAvisos && !error && (
        <p className="text-sm text-amber-800" data-testid="ia-con-avisos">
          La IA no pudo dejarlo sin avisos: revisalos y corregí {esMail ? "el texto" : "la plantilla"} antes de usar{esMail ? "lo" : "la"}.
        </p>
      )}
    </section>
  );
}
